import * as React from 'react';
import { ContextMenu, Menu, MenuItem } from '@blueprintjs/core';
import { TabPanel, Tab, TabPanelAlignment } from 'src/components/controls/Tab';
import moment from 'moment';
import TractDetail from 'src/models/tract_detail/TractDetail';
import Horizon from 'src/models/tract_detail/Horizon';
import Ownership from 'src/models/tract_detail/Ownership';
import Leasehold from 'src/models/leasehold/Leasehold';
import ContactNote from 'src/models/ContactNote';
import ControlSummary from './ControlSummary';
import { isBrineLayerId, LEASEHOLD_LAYER_ID, morFamilies, morHeld, IMorLease, IMorListing, IMorOwner } from './morData';
import { bucketContactPiece, CONTACT_CATEGORY_LEGEND_ORDER } from './DefaultContactCategoryMapColors';
import { canonicalTitleStatusValue } from './DefaultTitleStatusMapColors';
import { contractLayerFields, OIL_AND_GAS_LAYER_FIELDS, ownerNetAcres, ownershipCategories } from './ContractLayers';
import { contractLayerPrefixOf } from './MapConfig';

/* tslint:disable */
interface TractAttributeTableProps {
    tractId: string | undefined;
    geoJsonAttributes: Record<string, string | number> | undefined;
    tractDetail: TractDetail | undefined;
    /** Mineral ownerships for the selected tract/horizon, fetched from paged-ownerships
     *  (the tract-detail endpoint does not populate per-horizon ownership). */
    mineralOwnerships: Ownership[] | undefined;
    leaseholds: Leasehold[] | undefined;
    isLoadingDetail: boolean;
    isLoadingOwnerships: boolean;
    isLoadingLeaseholds: boolean;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    onNavigateToTractPage?: () => void;
    allTracts?: Record<string, string | number>[];
    onSelectTract?: (id: string, attributes: Record<string, string | number>) => void;
    /** Multi-select state — list of selected KuduTractIDs and a setter. When non-empty,
     *  the Tracts tab shows a header strip with a "Show only selected" toggle, Clear,
     *  and (Phase 3) bulk-action buttons. */
    selectedTractIds?: string[];
    onSelectedTractIdsChange?: (ids: string[]) => void;
    /** The map's tract layers; the Tracts list's WI owner columns follow its lease layers (see
     *  TractsListTab). Unknown (undefined): the oil and gas column shows. */
    tractLayerIds?: string[];
}

const formatDate = (val: Date | string | null | undefined): string => {
    if (!val) return '';
    const m = moment(val);
    return m.isValid() ? m.format('MM/DD/YYYY') : String(val);
};

const formatPercent = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '';
    const num = Number(val);
    if (isNaN(num)) return String(val);
    return (num * 100).toFixed(6) + '%';
};

const formatCurrency = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '';
    const num = Number(val);
    if (isNaN(num)) return String(val);
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatAcres = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '';
    const num = Number(val);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const isPresent = (val: unknown): boolean => val !== null && val !== undefined;

const yesNo = (val: unknown): string => (val === 1 || val === '1' || val === true ? 'Yes' : 'No');

/* WI owner display for the Tracts list. WI_Owner is a pipe-delimited list of names
 * (or "Unleased"). Show "Unleased" when none, the single name when one, and
 * "lead + N more" when multiple. */
const formatWiOwners = (raw: unknown): string => {
    const s = (raw === null || raw === undefined ? '' : String(raw)).trim();
    if (!s || s === 'Unleased') return 'Unleased';
    const names = s
        .split('|')
        .map((n) => n.trim())
        .filter((n) => n.length > 0);
    if (names.length === 0) return 'Unleased';
    if (names.length === 1) return names[0];
    return `${names[0]} +${names.length - 1} more`;
};

/* The tract's gross acres. Tract detail is authoritative (it's live); the baked
 * Legal_Acre carries the same Tract.GrossAcres value and covers the window before
 * detail lands. Used as the total that leasehold + open acreage add up to. */
const tractGrossAcres = (tractDetail: TractDetail | undefined, attributes: Record<string, string | number> | undefined): number | undefined => {
    const candidates = [tractDetail?.grossAcres, attributes?.Legal_Acre];
    for (const c of candidates) {
        if (c === null || c === undefined || c === '') continue;
        const n = Number(c);
        if (!isNaN(n) && n > 0) return n;
    }
    return undefined;
};

/*
 * Client acreage as a share of the tract, rather than a yes/no.
 *
 * Is_Client_Owned answers a narrower question than the old "Is Client Acreage" label implied:
 * it means "every WI owner on this tract is the client", so it reads Yes on a tract the client
 * holds 12.5 of 100 acres of. Showing the acreage says what the flag can't, and matches the
 * control banner. Falls back to the flag for files baked before WI_Client_NetAcres existed.
 */
const clientAcreageValue = (attributes: Record<string, string | number> | undefined, grossAcres: number | undefined): string => {
    const raw = attributes?.WI_Client_NetAcres;
    const clientNet = raw === null || raw === undefined || raw === '' ? undefined : Number(raw);

    if (clientNet === undefined || isNaN(clientNet)) {
        return yesNo(attributes?.Is_Client_Owned);
    }
    if (grossAcres === undefined || grossAcres <= 0) {
        return `${formatAcres(clientNet)} net ac`;
    }
    return `${formatAcres(clientNet)} of ${formatAcres(grossAcres)} ac (${Math.round((clientNet / grossAcres) * 100)}%)`;
};

/* The horizon that represents the tract in the summary: the (first) target horizon, else
 * the only one. Multiple non-target horizons have no single answer, so return undefined. */
const getRepresentativeHorizon = (tractDetail: TractDetail | undefined): Horizon | undefined => {
    const horizons: Horizon[] = tractDetail?.horizons || [];
    return horizons.find((h) => h.horizonInterval?.isTargetHorizon) || (horizons.length === 1 ? horizons[0] : undefined);
};

/* Which horizon's examined date represents the tract: the (first) IsTargetHorizon
 * when one is flagged; otherwise the most recently examined horizon. Guards against
 * multiple target horizons by taking the first. */
const getTractExaminedDate = (tractDetail: TractDetail | undefined): Date | string | undefined => {
    const horizons: Horizon[] = tractDetail?.horizons || [];
    if (horizons.length === 0) return undefined;

    const targets = horizons.filter((h) => h.horizonInterval?.isTargetHorizon);
    if (targets.length > 0) {
        return targets[0].examinedDate;
    }

    const withDates = horizons.filter((h) => !!h.examinedDate);
    if (withDates.length === 0) return undefined;
    return withDates.reduce((latest, h) => (moment(h.examinedDate).isAfter(moment(latest.examinedDate)) ? h : latest)).examinedDate;
};

const TractInfoTab: React.FC<{
    attributes: Record<string, string | number> | undefined;
    tractDetail: TractDetail | undefined;
    tractId: string | undefined;
    /** Title Status layer colors; only while that layer is active. */
    titleStatusColors?: Record<string, number[]>;
}> = ({ attributes, tractDetail, tractId, titleStatusColors }) => {
    if (!attributes && !tractDetail) {
        return <div className="attr-empty">Click a tract on the map to view its attributes.</div>;
    }

    const examined = getTractExaminedDate(tractDetail);
    const horizon = getRepresentativeHorizon(tractDetail);
    const gross = tractGrossAcres(tractDetail, attributes);
    const grossAcres = gross !== undefined ? String(gross) : '';
    const attr = (key: string): string => String(attributes?.[key] ?? '').trim();

    // The Location the tracts file carries (dbo.usp_GISMapTractAttributes'), as the Tract List shows
    // it: it follows the land system (Texas abstracts; PLSS township, range and section; the Eastern
    // map). The tract record can't stand in for it: Texas tracts' records carry a placeholder township
    // and range (T0 R0).
    const location = attr('Location');
    const horizonLabel = horizon
        ? [
              horizon.horizonName,
              isPresent(horizon.horizonBeginDepth) || isPresent(horizon.horizonEndDepth)
                  ? `(${horizon.horizonBeginDepth ?? '?'}–${horizon.horizonEndDepth ?? '?'} ft)`
                  : '',
          ]
              .filter(Boolean)
              .join(' ')
        : '';
    const vesting = horizon
        ? [horizon.instrumentType, [horizon.grantor, horizon.grantee].filter(Boolean).join(' → ')].filter(Boolean).join(': ')
        : '';
    // Where to find it: the horizon's Deed Reference (book/page or instrument number, free text as
    // entered on the Tract page) and Deed Date.
    const deedReference = horizon
        ? [String(horizon.sourceReference ?? '').trim(), horizon.sourceDate ? `dated ${formatDate(horizon.sourceDate)}` : '']
              .filter(Boolean)
              .join(' · ')
        : '';

    /* Every row shows, a blank one as "—", so each tract's panel has the same fields in the same
     * places. */
    const rows: Array<{ label: string; value: React.ReactNode }> = [
        { label: 'KuduTractID', value: String(tractId || attributes?.KuduTractID || attributes?.GlobalID || '') },
        { label: 'Tract Name', value: tractDetail?.tractName || attr('Tract_Name') },
        { label: 'Short Legal Description', value: tractDetail?.shortLegalDescription || '' },
        { label: 'Location', value: location },
        { label: 'County / State', value: attr('County_State') },
        { label: 'Parcel ID', value: tractDetail?.parcelID || '' },
        { label: 'Gross Acres', value: grossAcres },
        { label: 'Client Acreage', value: clientAcreageValue(attributes, gross) },
        { label: 'Lease Status', value: attr('Lease_Status') },
        { label: 'WI Owner', value: attr('WI_Owner') ? formatWiOwners(attributes?.WI_Owner) : '' },
        {
            label: 'Title Status',
            value: attr('Title_Status') ? (
                <SwatchText text={attr('Title_Status')} rgba={titleStatusColors?.[canonicalTitleStatusValue(attr('Title_Status'))]} />
            ) : (
                ''
            ),
        },
        { label: 'Horizon', value: horizonLabel },
        { label: 'Examined Date', value: examined ? formatDate(examined) : '' },
        { label: 'Examined By', value: horizon?.examinedByLandman || '' },
        { label: 'Vesting Instrument', value: vesting },
        { label: 'Deed Reference', value: deedReference },
        { label: 'Total Interest', value: isPresent(tractDetail?.totalInterest) ? formatPercent(tractDetail.totalInterest) : '' },
        { label: 'Tags', value: (tractDetail?.tags || []).join(', ') },
    ];
    const legal = tractDetail?.legalDescription || attr('Legal_Desc');
    const note = String(tractDetail?.note ?? '').trim();

    return (
        <div className="tract-info">
            <div className="attr-kv-grid">
                {rows.map(({ label, value }) => (
                    <React.Fragment key={label}>
                        <div className="attr-kv-label">{label}</div>
                        <div className="attr-kv-value">{value !== '' ? value : '—'}</div>
                    </React.Fragment>
                ))}
            </div>
            {/* Last, full width: legal descriptions and notes run long and read badly squeezed into the value column. */}
            <div className="tract-info-legal">
                <div className="tract-info-legal-label">Legal Description</div>
                <div className="tract-info-legal-text">{legal || '—'}</div>
            </div>
            {note && (
                <div className="tract-info-legal">
                    <div className="tract-info-legal-label">Note</div>
                    <div className="tract-info-legal-text">{note}</div>
                </div>
            )}
        </div>
    );
};

/* Contact Category with the same color swatch the map's Contact Category layer uses. Leased
 * ownerships usually have a blank category (in-handing clears it), so — like GISService does
 * when baking the GeoJSON — the caller passes "Leased" for them (see heldCategory). */
const ContactCategoryChip: React.FC<{ raw: string; colors: Record<string, number[]> | undefined }> = ({ raw, colors }) => {
    if (!raw) return <>—</>;
    return <SwatchText text={raw} rgba={colors?.[bucketContactPiece(raw)]} />;
};

/* How the Mineral Owners tab orders its cards: grouped by the simplified contact category the map
 * legend shows (the default), by name to find someone, or largest interest first. */
export type OwnerView = 'category' | 'name' | 'nma';
const OWNER_VIEWS: Array<{ view: OwnerView; label: string; title: string }> = [
    { view: 'category', label: 'Category', title: 'Grouped by contact category, as in the map legend' },
    { view: 'name', label: 'Name', title: 'Owners A to Z' },
    { view: 'nma', label: 'NMA', title: 'Largest net mineral interest first' },
];

/* The panel's Expand all / Collapse all (a right-click on a header). Cards open by default. A command
 * sets every card open or closed, and cards that mount afterwards (the next page, the next tract)
 * start the way it left them; version tells the cards a command came, even one that repeats the
 * last. Expand all also opens every collapsed group. showMenu opens the menu. */
interface ICardExpansion {
    open: boolean;
    version: number;
    showMenu?: (e: React.MouseEvent) => void;
}
const CardExpansionContext = React.createContext<ICardExpansion>({ open: true, version: 0 });

/* Which groups of a tab are collapsed, by group key. Groups start open, and each tract starts fresh
 * (its tabs remount); Expand all opens them all again. */
const useCollapsedGroups = (): [(key: string) => boolean, (key: string) => void] => {
    const expansion = React.useContext(CardExpansionContext);
    const [collapsed, setCollapsed] = React.useState<string[]>([]);
    const [seenVersion, setSeenVersion] = React.useState(expansion.version);
    if (seenVersion !== expansion.version) {
        setSeenVersion(expansion.version);
        if (expansion.open) {
            setCollapsed([]);
        }
    }
    const isCollapsed = (key: string): boolean => collapsed.indexOf(key) >= 0;
    const toggle = (key: string) => setCollapsed((keys) => (keys.indexOf(key) >= 0 ? keys.filter((k) => k !== key) : [...keys, key]));
    return [isCollapsed, toggle];
};

/* Net acres followed by their share of the tract's gross acres, e.g. "14.24 (10.5%)". The share
 * is omitted when gross acres aren't known. */
const AcresOfGross: React.FC<{ acres: number; gross: number | undefined }> = ({ acres, gross }) => (
    <>
        <span className="attr-acres">{formatAcres(acres)}</span>
        {gross !== undefined && gross > 0 && <span className="attr-acres-pct"> ({((acres / gross) * 100).toFixed(1)}%)</span>}
    </>
);

/* Text with an optional map-color square in front (RGBA, alpha already the layer opacity). */
const SwatchText: React.FC<{ text: string; rgba: number[] | undefined }> = ({ text, rgba }) => (
    <span className="attr-contact-chip">
        {rgba && <span className="attr-contact-swatch" style={{ background: `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3] ?? 1})` }} />}
        {text}
    </span>
);

/* Cards render a page at a time: a tract can carry hundreds of owners or leases. A sentinel after
 * the last card pulls in the next page as it scrolls into view (IntersectionObserver clips to the
 * panel's scroll area, so no scroll container needs wiring). Where it's missing (jsdom), show all. */
const CARD_PAGE_SIZE = 25;
/** Renders `cards` a page at a time. Remount it (via `key`) to start back at the first page. */
const PagedCards: React.FC<{ cards: React.ReactNode[] }> = ({ cards }) => {
    const total = cards.length;
    const canObserve = typeof window !== 'undefined' && 'IntersectionObserver' in window;
    const [limit, setLimit] = React.useState(CARD_PAGE_SIZE);
    const sentinelRef = React.useRef<HTMLDivElement>(null);
    const hasMore = canObserve && limit < total;
    React.useEffect(() => {
        const el = sentinelRef.current;
        if (!hasMore || !el) {
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                setLimit((n) => n + CARD_PAGE_SIZE);
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMore, limit]);
    return (
        <>
            {canObserve ? cards.slice(0, limit) : cards}
            {hasMore && (
                <div ref={sentinelRef} className="attr-card-more">
                    Showing {limit} of {total}…
                </div>
            )}
        </>
    );
};

/* One Mineral Owners card: the MOR's row for the owner when the report's listing is in, joined to
 * the stored ownership (contact category, owner record, contact notes) by ownership id. Without the
 * listing, the stored ownership alone, with its stored leased status. */
interface OwnerCard {
    ownershipId: number;
    ownerLookUpId?: number;
    ownership: Ownership | undefined;
    name: string;
    /* MI, HEIR, REMAINDERMAN, NPRI, ... (the MOR's); blank without the listing. */
    category: string;
    decimalInterest: number | undefined;
    netAcres: number | undefined;
    /* The MOR's status, dated today; without the listing, the stored LeasedStatus. */
    status: string;
    held: boolean;
    leaseNumber: string;
    /* The owner's contact category as the map colors by it: one something holds reads Leased or
     * Leased to Others. Without the map's, the owner's own, a held owner with none reading Leased. */
    contactCategory: string;
    isChild: boolean;
    /* The owner's contact note count from the MOR listing; unknown without it. */
    noteCount?: number;
}
type OwnerFamily = { head: OwnerCard; children: OwnerCard[] };

const storedOwnerName = (o: Ownership | undefined): string => (o ? o.ownerInfo?.name || `${o.firstName || ''} ${o.lastName || ''}`.trim() : '');
const toNumber = (value: unknown): number | undefined =>
    value === null || value === undefined || value === '' || isNaN(Number(value)) ? undefined : Number(value);
const heldCategory = (contactCategory: string | undefined, held: boolean): string => (contactCategory || '').trim() || (held ? 'Leased' : '');
/* The MOR's additional type for a non-executive interest: in full on its owner listing, NEMI on its
 * other sheets. */
const isNonExecutiveCategory = (category: string | null | undefined): boolean => {
    const type = (category || '').trim().toUpperCase();
    return type === 'NEMI' || type.indexOf('NON-EXECUTIVE') >= 0;
};

/* The cards as families: from the MOR listing, each additional ownership nested under its parent the
 * way the report lists it; without the listing, one card per stored ownership. Each owner's contact
 * category is the map's for its ownership (mapCategories, from the tract's Ownership_Categories), so
 * the cards group the way the map colors the tract; without it, the owner's own. */
const ownerFamilies = (
    morRows: IMorOwner[] | undefined,
    ownerships: Ownership[],
    mapCategories: Map<number, string> = new Map<number, string>()
): OwnerFamily[] => {
    const stored = new Map<number, Ownership>();
    ownerships.forEach((o) => {
        if (o) {
            stored.set(o.id, o);
        }
    });
    if (morRows && morRows.length > 0) {
        /* A non-executive interest has no executive rights: its executive, the parent, leases it along
         * with its own interest. So under a held parent it's held too and reads the parent's status,
         * as the map counts it (usp_GISMapTractAttributes); the MOR lists it as Open. */
        const card = (row: IMorOwner, isChild: boolean, executive?: OwnerCard): OwnerCard => {
            const ownership = stored.get(row.ownershipId);
            const ownHeld = morHeld(row.status);
            const leasesWithExecutive = !ownHeld && !!executive && executive.held && isNonExecutiveCategory(row.category);
            const held = ownHeld || leasesWithExecutive;
            return {
                ownershipId: row.ownershipId,
                ownerLookUpId: row.ownerLookUpId ?? ownership?.ownerLookUpId,
                ownership,
                name: (row.ownerName || '').trim() || storedOwnerName(ownership),
                category: (row.category || '').trim(),
                decimalInterest: toNumber(row.decimalInterest),
                netAcres: toNumber(row.netAcres),
                status: leasesWithExecutive && executive ? executive.status : (row.status || '').trim(),
                held,
                leaseNumber: (row.leaseNumber || '').trim(),
                contactCategory: mapCategories.get(row.ownershipId) ?? heldCategory(ownership?.contactCategory, held),
                isChild,
                noteCount: toNumber(row.noteCount),
            };
        };
        return morFamilies(morRows).map((family) => {
            const head = card(family.head, false);
            return { head, children: family.children.map((child) => card(child, true, head)) };
        });
    }
    return ownerships.filter(Boolean).map((o) => {
        const held = isLeasedStatus(o.leasedStatus);
        return {
            head: {
                ownershipId: o.id,
                ownerLookUpId: o.ownerLookUpId,
                ownership: o,
                name: storedOwnerName(o),
                category: '',
                decimalInterest: toNumber(o.decimalInterest),
                netAcres: toNumber(o.netAcres),
                status: (o.leasedStatus || '').trim(),
                held,
                leaseNumber: '',
                contactCategory: mapCategories.get(o.id) ?? heldCategory(o.contactCategory, held),
                isChild: false,
            },
            children: [],
        };
    });
};

/* Whether a card's interest counts toward a total, by the app's acreage rollup (as the map counts
 * ownerships, and Tract Detail's total interest): every top-level owner, an heir and a non-executive
 * interest. A remainderman, a non-participating interest, a transfer-on-death beneficiary or a naked
 * owner never counts, even under a parent that holds nothing. */
const countsTowardTotals = (card: OwnerCard): boolean => {
    if (!card.isChild) {
        return true;
    }
    const category = card.category.toUpperCase();
    return category === 'HEIR' || category === 'NEMI' || category.indexOf('NON-EXECUTIVE') >= 0;
};

/* The additional ownerships that fold into their parent's card rather than get one of their own, as
 * Tract Detail nests them: a remainderman, a non-participating interest, a transfer-on-death
 * beneficiary or a naked owner. Heirs and non-executive interests keep their own cards. (The MOR
 * itself lists every one under its parent.) Its owner listing names a type in full; its other
 * sheets use the short forms. */
const FOLDED_TYPES = ['REMAINDERMAN', 'NON-PARTICIPATING', 'TRANSFER ON DEATH', 'NAKED OWNER'];
const FOLDED_SHORT_FORMS = ['REM', 'NPRI', 'TOD', 'NAK'];
const foldsIntoParent = (card: OwnerCard): boolean => {
    if (!card.isChild) {
        return false;
    }
    const category = card.category.toUpperCase();
    return FOLDED_SHORT_FORMS.indexOf(category) >= 0 || FOLDED_TYPES.some((type) => category.indexOf(type) >= 0);
};

const MineralOwnersTab: React.FC<{
    ownerships: Ownership[] | undefined;
    isLoading: boolean;
    /** The MOR's owner listing for this tract (see Mapping's handleMorShown), which the cards read. */
    morOwners?: IMorListing<IMorOwner>;
    /** Asks for the MOR listing when the tab shows. Without it, or while the listing can't be had,
     *  the cards read the stored ownerships. */
    onMorShown?: () => void;
    contactCategoryColors?: Record<string, number[]>;
    /** Show only interests nothing holds (Open or Expired in the MOR). Owned by the panel so it
     *  sticks while clicking from tract to tract. */
    openOnly: boolean;
    onOpenOnlyChange: (value: boolean) => void;
    /** Card order (see OwnerView); owned by the panel, like openOnly. */
    view: OwnerView;
    onViewChange: (view: OwnerView) => void;
    /** Loads one owner's contact notes; called only when someone asks for them (View notes), or opens
     *  the card of an owner whose note count the listing didn't give. */
    onLoadContactNotes: (ownership: Ownership) => Promise<ContactNote[]>;
    /** Each ownership's contact category as the map colors the tract by it (see ownerFamilies). */
    mapCategories?: Map<number, string>;
}> = ({
    ownerships,
    isLoading,
    morOwners,
    onMorShown,
    contactCategoryColors,
    openOnly,
    onOpenOnlyChange,
    view,
    onViewChange,
    onLoadContactNotes,
    mapCategories,
}) => {
    // Each owner card shows its details, then a Contact notes line with the owner's note count (from
    // the MOR listing) and View notes, grayed out when the owner has none. The notes themselves load
    // only when someone asks, never for every card up front, and are kept for the tract, so asking
    // again doesn't fetch again. Without the listing's count, opening a card again loads them to count
    // them (Expand all doesn't). View notes swaps the list for the owner's card and notes, with Back
    // at the top. The list stays mounted (just hidden), so Back returns to the same scroll position,
    // open cards and loaded pages.
    const [openOwner, setOpenOwner] = React.useState<OwnerCard | null>(null);
    const [notesByOwner, setNotesByOwner] = React.useState<Record<number, OwnerNotesState>>({});
    const notesByOwnerRef = React.useRef<Record<number, OwnerNotesState>>({});
    const mountedRef = React.useRef(true);
    const rootRef = React.useRef<HTMLDivElement>(null);
    const listScrollTopRef = React.useRef(0);
    const [isGroupCollapsed, toggleGroup] = useCollapsedGroups();
    const scroller = (): HTMLElement | null => (rootRef.current ? (rootRef.current.closest('.lh-tab-content') as HTMLElement | null) : null);

    // The tab mounts only when it shows, so this asks for the MOR listing when someone looks.
    React.useEffect(() => {
        if (onMorShown) {
            onMorShown();
        }
    }, [onMorShown]);
    // Notes that land after the tab is gone (another tract, another tab) are dropped.
    React.useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );
    // The notes view starts at the top; Back puts the list where it was.
    React.useLayoutEffect(() => {
        const el = scroller();
        if (el) {
            el.scrollTop = openOwner ? 0 : listScrollTopRef.current;
        }
    }, [openOwner]);

    const setOwnerNotes = (ownershipId: number, state: OwnerNotesState) => {
        notesByOwnerRef.current = { ...notesByOwnerRef.current, [ownershipId]: state };
        setNotesByOwner(notesByOwnerRef.current);
    };
    // The owner record the notes loader takes: the stored ownership, or its ids from the MOR.
    const notesOwnership = (c: OwnerCard): Ownership => c.ownership || ({ id: c.ownershipId, ownerLookUpId: c.ownerLookUpId } as Ownership);
    // Loads an owner's notes unless they're loaded or on their way; a failed load is tried again.
    const loadOwnerNotes = (c: OwnerCard) => {
        const current = notesByOwnerRef.current[c.ownershipId];
        if (current && current.status !== 'error') {
            return;
        }
        setOwnerNotes(c.ownershipId, { status: 'loading', notes: [] });
        onLoadContactNotes(notesOwnership(c)).then(
            (loaded) => {
                if (mountedRef.current) setOwnerNotes(c.ownershipId, { status: 'ready', notes: loaded });
            },
            () => {
                if (mountedRef.current) setOwnerNotes(c.ownershipId, { status: 'error', notes: [] });
            }
        );
    };
    const openOwnerNotes = (c: OwnerCard) => {
        listScrollTopRef.current = scroller()?.scrollTop ?? 0;
        setOpenOwner(c);
        loadOwnerNotes(c);
    };
    const closeOwnerNotes = () => setOpenOwner(null);

    // The stored ownerships show at once; the MOR's listing takes their place when it lands, so the
    // report (about a second on a brine tract) never holds the tab.
    if (isLoading) return <div className="attr-loading">Loading mineral ownership data...</div>;
    const morFailed = !!onMorShown && !!morOwners && morOwners.status === 'error';
    const families = ownerFamilies(morOwners && morOwners.status === 'ready' ? morOwners.rows : undefined, ownerships || [], mapCategories);
    if (families.length === 0) {
        return <div className="attr-empty">No mineral owners found for this tract.</div>;
    }

    const members = (f: OwnerFamily): OwnerCard[] => [f.head].concat(f.children);
    // A family's interest, counting each member the family's way (countsTowardTotals).
    const familyTotal = (f: OwnerFamily, field: 'decimalInterest' | 'netAcres', openMembersOnly = false): number =>
        members(f)
            .filter((c) => countsTowardTotals(c) && (!openMembersOnly || !c.held))
            .reduce((sum, c) => sum + (c[field] || 0), 0);
    // Under a category heading the heading carries the category's color, so the card doesn't repeat it.
    // Every field, a blank one as a dash, so each card has the same fields in the same places.
    const ownerRows = (c: OwnerCard, withSwatch = true): KvRow[] => [
        { label: 'Interest Type', value: c.category },
        { label: 'Decimal Interest', value: formatPercent(c.decimalInterest) },
        { label: 'NMA', value: formatAcres(c.netAcres) },
        { label: 'Status', value: c.status },
        { label: 'Lease #', value: c.leaseNumber },
        { label: 'Contact Category', value: <ContactCategoryChip raw={c.contactCategory} colors={withSwatch ? contactCategoryColors : undefined} /> },
    ];
    // NMA: the family with the largest interest first (decimal interest, then net mineral acres),
    // then by name. Name: A to Z by the family's head. Category (the default) keeps the NMA order
    // within each category group. Children always follow their parent, in the report's order.
    const byName = (a: OwnerFamily, b: OwnerFamily): number => a.head.name.localeCompare(b.head.name, undefined, { sensitivity: 'base' });
    const bySize = (a: OwnerFamily, b: OwnerFamily): number =>
        familyTotal(b, 'decimalInterest') - familyTotal(a, 'decimalInterest') ||
        familyTotal(b, 'netAcres') - familyTotal(a, 'netAcres') ||
        byName(a, b);
    const sortedFamilies = [...families].sort(
        view === 'name' ? (a, b) => byName(a, b) || familyTotal(b, 'netAcres') - familyTotal(a, 'netAcres') : bySize
    );
    const allCards = families.reduce((all, f) => all.concat(members(f)), [] as OwnerCard[]);
    // Open: an interest that counts (countsTowardTotals) and nothing holds. A remainderman, NPRI,
    // transfer-on-death beneficiary or naked owner reads Open on the MOR with nothing to lease.
    const isOpen = (c: OwnerCard): boolean => countsTowardTotals(c) && !c.held;
    const countedCount = allCards.filter(countsTowardTotals).length;
    const openCount = allCards.filter(isOpen).length;
    const openNma = families.reduce((sum, f) => sum + familyTotal(f, 'netAcres', true), 0);
    // Open interests only: families with an open member, each still under its head for context.
    const shownFamilies = openOnly
        ? sortedFamilies.filter((f) => members(f).some(isOpen)).map((f) => ({ head: f.head, children: f.children.filter(isOpen) }))
        : sortedFamilies;

    // The Contact notes line at the bottom of an owner card: the owner's note count and View notes,
    // grayed out when there are none. The count comes with the MOR listing, or from the notes once
    // loaded; without either, View notes is there to find out.
    const notesLine = (c: OwnerCard) => {
        const state = notesByOwner[c.ownershipId];
        const count = state ? (state.status === 'ready' ? uniqueNotes(state.notes).length : undefined) : c.noteCount;
        return (
            <div className="owner-notes-box">
                <span className="owner-notes-box-label">Contact notes</span>
                {state && state.status === 'loading' && <span className="attr-muted-note">loading…</span>}
                {state && state.status === 'error' && (
                    <>
                        <span className="attr-muted-note">couldn&apos;t load</span>
                        <button type="button" className="attribute-table-link-btn" onClick={() => loadOwnerNotes(c)}>
                            Try again
                        </button>
                    </>
                )}
                {(!state || state.status === 'ready') && (
                    <>
                        {count !== undefined && <span className="owner-notes-box-count">{count}</span>}
                        <button
                            type="button"
                            className="attribute-table-link-btn"
                            disabled={count === 0}
                            title={count === 0 ? 'This owner has no contact notes' : undefined}
                            onClick={() => openOwnerNotes(c)}
                        >
                            View notes ›
                        </button>
                    </>
                )}
            </div>
        );
    };
    // One line per owner: an additional ownership's category leads its figure (NMA, or the decimal
    // where the report counts no acres, as for an NPRI).
    const cardAside = (c: OwnerCard): React.ReactNode => {
        const figure = c.netAcres !== undefined ? `${formatAcres(c.netAcres)} NMA` : formatPercent(c.decimalInterest);
        return c.isChild && c.category ? (
            <>
                <span className="attr-category-tag">{c.category}</span> {figure}
            </>
        ) : (
            figure
        );
    };
    // An additional ownership folded into its parent's card: its name and figure, its T-chart and its
    // Contact notes line.
    const foldedCard = (c: OwnerCard, grouped: boolean): React.ReactNode => (
        <div key={`folded-${c.ownershipId}`} className="owner-folded-card">
            <div className="owner-folded-title">
                <span className="owner-folded-name">{c.name || '—'}</span>
                <span className="attr-card-aside">{cardAside(c)}</span>
            </div>
            <KvGrid rows={ownerRows(c, !grouped)} />
            {notesLine(c)}
        </div>
    );
    // A family's cards: the head's, then one for each heir or non-executive interest. The other
    // additional ownerships fold into the head's card (foldsIntoParent), closed until someone opens them.
    const familyCards = (f: OwnerFamily, grouped: boolean): React.ReactNode[] => {
        const folded = f.children.filter(foldsIntoParent);
        const categories = folded.map((c) => c.category).filter((category, i, all) => all.indexOf(category) === i);
        return [f.head, ...f.children.filter((c) => !foldsIntoParent(c))].map((c, i) => (
            <ExpandableCard
                key={`owner-${c.ownershipId}-${i}`}
                className={`owner-card${grouped ? ' attr-group-card' : ''}${c.isChild ? ' owner-card-child' : ''}`}
                title={c.name || '—'}
                aside={cardAside(c)}
                rows={ownerRows(c, !grouped)}
                footer={
                    <>
                        {notesLine(c)}
                        {c === f.head && folded.length > 0 && (
                            <FoldedOwnerships count={folded.length} categories={categories}>
                                {folded.map((child) => foldedCard(child, grouped))}
                            </FoldedOwnerships>
                        )}
                    </>
                }
                onOpen={c.noteCount === undefined ? () => loadOwnerNotes(c) : undefined}
            />
        ));
    };
    // Category: a heading per simplified contact category (the bucket the map colors the owner by, from
    // the family's head), in the legend's order with Other last, each with its owner count and NMA.
    // An owner nothing holds and no one has given a category goes under Other, as the map colors it.
    let cards: React.ReactNode[];
    if (view === 'category') {
        const byStatus = new Map<string, OwnerFamily[]>();
        shownFamilies.forEach((f) => {
            const status = bucketContactPiece(f.head.contactCategory);
            byStatus.set(status, [...(byStatus.get(status) || []), f]);
        });
        const rank = (status: string): number => {
            const i = CONTACT_CATEGORY_LEGEND_ORDER.indexOf(status);
            return i >= 0 ? i : CONTACT_CATEGORY_LEGEND_ORDER.length;
        };
        cards = [];
        Array.from(byStatus.keys())
            .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
            .forEach((status) => {
                const groupFamilies = byStatus.get(status) || [];
                const owners = groupFamilies.reduce((n, f) => n + members(f).length, 0);
                const nma = groupFamilies.reduce((sum, f) => sum + familyTotal(f, 'netAcres', openOnly), 0);
                cards.push(
                    <GroupHeading
                        key={`status-${status}`}
                        name={status}
                        rgba={contactCategoryColors?.[status]}
                        summary={`${owners} ${owners === 1 ? 'owner' : 'owners'} · ${formatAcres(nma)} NMA`}
                        collapsed={isGroupCollapsed(status)}
                        onToggle={() => toggleGroup(status)}
                    />
                );
                if (!isGroupCollapsed(status)) {
                    groupFamilies.forEach((f) => cards.push(...familyCards(f, true)));
                }
            });
    } else {
        cards = shownFamilies.reduce((all, f) => all.concat(familyCards(f, false)), [] as React.ReactNode[]);
    }

    return (
        <div className="attr-card-list" ref={rootRef}>
            <div className="owners-list-pane" style={openOwner ? { display: 'none' } : undefined}>
                <div className="owners-filter-strip">
                    <span className="owners-filter-controls">
                        <label className="owners-filter-toggle">
                            <input type="checkbox" checked={openOnly} onChange={(e) => onOpenOnlyChange(e.target.checked)} />
                            Open interests only
                        </label>
                        <span className="owners-view-switch" role="group" aria-label="Order owners by">
                            {OWNER_VIEWS.map(({ view: option, label, title }) => (
                                <button
                                    key={option}
                                    type="button"
                                    className={`owners-view-btn${view === option ? ' is-active' : ''}`}
                                    aria-pressed={view === option}
                                    title={title}
                                    onClick={() => onViewChange(option)}
                                >
                                    {label}
                                </button>
                            ))}
                        </span>
                    </span>
                    <span className="owners-filter-count">
                        {openCount} of {countedCount} open · {formatAcres(openNma)} NMA
                    </span>
                </div>
                {morFailed && (
                    <div className="owners-mor-note">
                        Couldn&apos;t load the ownership report for this tract, so these cards show stored statuses.{' '}
                        <button type="button" className="attribute-table-link-btn" onClick={onMorShown}>
                            Try again
                        </button>
                    </div>
                )}
                {shownFamilies.length === 0 && <div className="attr-empty">Every mineral interest on this tract is leased.</div>}
                <PagedCards key={`${openOnly ? 'open' : 'all'}-${view}`} cards={cards} />
            </div>
            {openOwner && (
                <div className="owner-notes-pane">
                    <button type="button" className="owner-notes-back" onClick={closeOwnerNotes}>
                        ‹ All owners
                    </button>
                    <TChartCard title={openOwner.name || '—'} rows={ownerRows(openOwner)} />
                    <ContactNotesList state={notesByOwner[openOwner.ownershipId] || { status: 'loading', notes: [] }} />
                </div>
            )}
        </div>
    );
};

type OwnerNotesState = { status: 'loading' | 'ready' | 'error'; notes: ContactNote[] };

/* An owner's contact notes, newest first, each once. The owner endpoint returns a row per ownership
 * a note is attached to, so the same note can arrive more than once. */
const uniqueNotes = (notes: ContactNote[]): ContactNote[] => {
    const byId = new Map<number, ContactNote>();
    notes.forEach((n) => {
        if (n && !byId.has(n.id)) byId.set(n.id, n);
    });
    const time = (d: Date | string | null | undefined): number => (d ? moment(d).valueOf() || 0 : 0);
    return Array.from(byId.values()).sort((a, b) => time(b.createdDate) - time(a.createdDate) || b.id - a.id);
};

/* One owner's contact notes, newest first, each shown once. */
const ContactNotesList: React.FC<{ state: OwnerNotesState }> = ({ state }) => {
    if (state.status === 'loading') return <div className="attr-loading">Loading contact notes…</div>;
    if (state.status === 'error') return <div className="attr-empty">Couldn&apos;t load contact notes.</div>;

    const notes = uniqueNotes(state.notes);

    return (
        <div className="owner-notes">
            <div className="owner-notes-heading">
                Contact notes <span className="owner-notes-count">{notes.length}</span>
            </div>
            {notes.length === 0 ? (
                <div className="attr-empty">No contact notes for this owner.</div>
            ) : (
                notes.map((n) => {
                    const edited = !!n.modifiedDate && formatDate(n.modifiedDate) !== formatDate(n.createdDate);
                    return (
                        <div key={n.id} className="owner-note">
                            <div className="owner-note-date">
                                {formatDate(n.createdDate) || 'Undated'}
                                {edited && <span className="attr-muted-note"> · edited {formatDate(n.modifiedDate)}</span>}
                            </div>
                            <div className="owner-note-text">{n.note}</div>
                        </div>
                    );
                })
            )}
        </div>
    );
};

type KvRow = { label: string; value: React.ReactNode };

/* A record's label | value T-chart. */
const KvGrid: React.FC<{ rows: KvRow[] }> = ({ rows }) => (
    <div className="attr-kv-grid">
        {rows.map(({ label, value }) => (
            <React.Fragment key={label}>
                <div className="attr-kv-label">{label}</div>
                <div className="attr-kv-value">{value !== '' ? value : '—'}</div>
            </React.Fragment>
        ))}
    </div>
);

/* One record in the narrow detail drawer: a title line, then a label | value T-chart.
 * Replaces the wide multi-column tables, which don't fit beside the map. */
const TChartCard: React.FC<{ title: string; rows: KvRow[]; className?: string }> = ({ title, rows, className }) => (
    <div className={`attr-card ${className || ''}`}>
        <div className="attr-card-title">{title}</div>
        <KvGrid rows={rows} />
    </div>
);

/* A heading over a group of cards (a WI owner's leases, a contact category's owners): its name with
 * the map color when that layer is showing, and a summary at the right. Given onToggle, it's a
 * button that collapses and opens its group. A heading with nothing to collapse can keep the
 * caret's room (caretSpace), so its swatch lines up under the ones that collapse. */
const GroupHeading: React.FC<{
    name: string;
    rgba?: number[];
    summary: React.ReactNode;
    collapsed?: boolean;
    onToggle?: () => void;
    caretSpace?: boolean;
}> = ({ name, rgba, summary, collapsed, onToggle, caretSpace }) => {
    const { showMenu } = React.useContext(CardExpansionContext);
    const content = (
        <>
            {onToggle ? (
                <span className="attr-card-caret" aria-hidden="true">
                    {collapsed ? '▸' : '▾'}
                </span>
            ) : (
                caretSpace && <span className="attr-group-caret-space" aria-hidden="true" />
            )}
            <span className="attr-group-name">{rgba ? <SwatchText text={name} rgba={rgba} /> : name}</span>
            <span className="attr-group-summary">{summary}</span>
        </>
    );
    return onToggle ? (
        <button type="button" className="attr-group-heading attr-group-toggle" aria-expanded={!collapsed} onClick={onToggle} onContextMenu={showMenu}>
            {content}
        </button>
    ) : (
        <div className="attr-group-heading" onContextMenu={showMenu}>
            {content}
        </div>
    );
};

/* A record that opens into its T-chart, with an optional footer below it, and closes to one line: its
 * title and a figure. It starts open, or the way the panel's last Expand all / Collapse all left the
 * cards, and follows the next one. onOpen runs each time someone opens it (not when a command does,
 * so Expand all never fetches per card). */
const ExpandableCard: React.FC<{
    title: string;
    aside?: React.ReactNode;
    rows: KvRow[];
    className?: string;
    footer?: React.ReactNode;
    onOpen?: () => void;
}> = ({ title, aside, rows, className, footer, onOpen }) => {
    const expansion = React.useContext(CardExpansionContext);
    const [open, setOpen] = React.useState(expansion.open);
    const [seenVersion, setSeenVersion] = React.useState(expansion.version);
    if (seenVersion !== expansion.version) {
        setSeenVersion(expansion.version);
        setOpen(expansion.open);
    }
    const toggle = () => {
        if (!open && onOpen) {
            onOpen();
        }
        setOpen(!open);
    };
    return (
        <div className={`attr-card attr-card-expandable ${open ? 'is-open' : ''} ${className || ''}`}>
            <button
                type="button"
                className="attr-card-title attr-card-toggle"
                aria-expanded={open}
                onClick={toggle}
                onContextMenu={expansion.showMenu}
            >
                <span className="attr-card-caret" aria-hidden="true">
                    {open ? '▾' : '▸'}
                </span>
                <span className="attr-card-toggle-text">{title}</span>
                {aside && <span className="attr-card-aside">{aside}</span>}
            </button>
            {open && <KvGrid rows={rows} />}
            {open && footer}
        </div>
    );
};

/* The additional ownerships folded into a parent's owner card (see foldsIntoParent): a line with how
 * many and their types, which opens to each one. It starts closed, or the way the panel's last Expand
 * all / Collapse all left the cards, and follows the next one. */
const FoldedOwnerships: React.FC<{ count: number; categories: string[] }> = ({ count, categories, children }) => {
    const expansion = React.useContext(CardExpansionContext);
    const [open, setOpen] = React.useState(expansion.version > 0 && expansion.open);
    const [seenVersion, setSeenVersion] = React.useState(expansion.version);
    if (seenVersion !== expansion.version) {
        setSeenVersion(expansion.version);
        setOpen(expansion.open);
    }
    return (
        <div className="owner-folded">
            <button
                type="button"
                className="owner-folded-toggle"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
                onContextMenu={expansion.showMenu}
            >
                <span className="attr-card-caret" aria-hidden="true">
                    {open ? '▾' : '▸'}
                </span>
                <span className="owner-folded-label">
                    {count} additional {count === 1 ? 'ownership' : 'ownerships'}
                </span>
                {categories.map((category) => (
                    <span key={category} className="attr-category-tag">
                        {category}
                    </span>
                ))}
            </button>
            {open && children}
        </div>
    );
};

/* Open acreage under this many acres reads as none — mineral interests that sum to
 * 0.999999 would otherwise show a sliver of "open" on a fully leased tract. Mirrors
 * GISService.OpenAcresEpsilon so the tab, the banner and the map hatch agree. */
const OPEN_ACRES_EPSILON = 0.05;

/*
 * Ownership.LeasedStatus values that mean the interest is genuinely under lease. Mirrors
 * LeasedStatusList in GISDataAccess.cs, which in turn mirrors dbo.setLeasedStatus: an
 * ownership only reaches 'Leased' / 'HBP' / 'Leased w/n Opt' when it has an ACTIVE leasehold
 * whose Lease.IsInHand = 1 and whose term covers today. 'Open' and 'Expired' are open
 * acreage. ('Leased w/Opt', 'HBU' and 'Active' are legacy spellings still in the data.)
 *
 * A leasehold row with isActiveLease = 1 is NOT sufficient on its own — LMSProd has 3,483
 * active leaseholds on leases that were never taken in hand, which is why open acreage has
 * to be measured from ownership status rather than from the lease rows in this table.
 */
const LEASED_STATUSES = ['leased', 'leased w/n opt', 'leased w/opt', 'hbp', 'hbu', 'active'];

const isLeasedStatus = (status: string | undefined): boolean => LEASED_STATUSES.indexOf((status || '').trim().toLowerCase()) >= 0;

/* What the Leaseholds tab reads from each leasehold's parent Lease (GET leases/{leaseId}), keyed
 * by lease id. Leases generated in LandHawk leave Leasehold.LeaseDate empty or stale, so the
 * Lease's own date wins; the lessee stands in when no WI owner was recorded. */
export interface LeaseInfo {
    leaseDate?: Date | string | null;
    lesseeName?: string | null;
}

const LeaseholdsTab: React.FC<{
    leaseholds: Leasehold[] | undefined;
    leaseInfo: Record<number, LeaseInfo>;
    /** Told the tract's leaseholds when this tab is showing them (after its own data has loaded), so
     *  parent-Lease lookups only happen for a tract someone actually views here. Stable identity. */
    onShown: (leaseholds: Leasehold[]) => void;
    isLoading: boolean;
    /** Mineral ownerships for the SELECTED HORIZON — the source of each lease's net acres. */
    ownerships: Ownership[] | undefined;
    /** Tract gross acres: the total the leases are measured against. */
    grossAcres: number | undefined;
    /** Leasehold layer WI owner colors, only while that layer is active (see MapLegacy onLeaseholdColors). */
    leaseholdColors?: Record<string, number[]>;
    /** The MOR's oil and gas lease listing for this tract, which fills each lease card (matched on
     *  leasehold id): status in the report's words, terms, HBP/HBU, recording, operator. */
    morLeases?: IMorListing<IMorLease>;
    /** Asks for the MOR listing when the tab shows leases. Without it, or while the listing can't be
     *  had, the cards read the stored lease records. */
    onMorShown?: () => void;
    /** The MOR's owner listing for this tract: what holds each interest today, which the leased and
     *  open acreage count from, as Mineral Owners does. */
    morOwners?: IMorListing<IMorOwner>;
    /** Asks for the MOR owner listing when the tab shows. Without it, or while the listing can't be
     *  had, the acreage counts the stored leased statuses. */
    onMorOwnersShown?: () => void;
}> = ({ leaseholds, leaseInfo, onShown, isLoading, ownerships, grossAcres, leaseholdColors, morLeases, onMorShown, morOwners, onMorOwnersShown }) => {
    // The tab panel mounts only the visible tab, so this runs when Leaseholds is open, and only once
    // its own data (leaseholds + ownerships) is in, so parent-Lease lookups never hold up the cards.
    React.useEffect(() => {
        if (!isLoading && leaseholds && leaseholds.length > 0) {
            onShown(leaseholds);
        }
    }, [leaseholds, isLoading, onShown]);
    const hasLeases = !!leaseholds && leaseholds.length > 0;
    React.useEffect(() => {
        if (onMorShown && !isLoading && hasLeases) {
            onMorShown();
        }
    }, [hasLeases, isLoading, onMorShown]);
    const hasOwnerships = !!ownerships && ownerships.length > 0;
    React.useEffect(() => {
        if (onMorOwnersShown && !isLoading && hasOwnerships) {
            onMorOwnersShown();
        }
    }, [hasOwnerships, isLoading, onMorOwnersShown]);
    const [isGroupCollapsed, toggleGroup] = useCollapsedGroups();

    const wiOwnerColor = (name: string): number[] | undefined => (leaseholdColors ? leaseholdColors[name] ?? leaseholdColors.__OTHER__ : undefined);
    // The stored leaseholds and owner statuses show at once; the MOR's listings take their place when
    // they land, so the report never holds the tab.
    if (isLoading) return <div className="attr-loading">Loading leasehold data...</div>;
    if (!leaseholds) return <div className="attr-empty">Click a tract to view leaseholds.</div>;
    const morFailed = !!onMorShown && hasLeases && !!morLeases && morLeases.status === 'error';
    const morOwnersFailed = !!onMorOwnersShown && hasOwnerships && !!morOwners && morOwners.status === 'error';
    // The MOR's row for each lease, by leasehold id. Its lease section lists a lease once, under one of
    // the lease's leaseholds (1510.1.1.77-1: three Browns on one lease, listed under one of them), so
    // the lease's other leaseholds take that row too, found through the lease they share.
    const morById = new Map<number, IMorLease>();
    const morByLeaseId = new Map<number, IMorLease>();
    if (morLeases && morLeases.status === 'ready') {
        morLeases.rows.forEach((row) => morById.set(row.leaseholdId, row));
        leaseholds.forEach((lh) => {
            const row = morById.get(lh.id);
            if (row && lh.leaseId) {
                morByLeaseId.set(lh.leaseId, row);
            }
        });
    }

    const infoFor = (lh: Leasehold): LeaseInfo | undefined => (lh.leaseId ? leaseInfo[lh.leaseId] : undefined);
    // No WI owner recorded: the lease's lessee, from its parent Lease once that has loaded.
    const lesseeOf = (lh: Leasehold): string => (infoFor(lh)?.lesseeName || '').trim();

    const lessorName = (lh: Leasehold): string =>
        lh.mineralOwner ? (lh.mineralOwner.ownerName ? lh.mineralOwner.ownerName() : lh.mineralOwner.name || '') : '';

    // Ownership lookup by id. Leaseholds come back for the whole tract while ownerships are
    // per-horizon, so this map doubles as the horizon filter: a lease whose ownership isn't
    // here belongs to another horizon and can't be measured against this horizon's acreage.
    const ownershipById: Record<number, Ownership> = {};
    (ownerships || []).forEach((o) => {
        if (o?.id != null) {
            ownershipById[o.id] = o;
        }
    });

    // Net mineral acres of the ownership a lease sits on. paged-ownerships computes NetAcres
    // as mineral interest x tract gross acres; recompute here only if it came back null.
    const ownershipNetAcres = (o: Ownership | undefined): number | undefined => {
        if (!o) return undefined;
        if (o.netAcres != null && !isNaN(Number(o.netAcres))) return Number(o.netAcres);
        if (o.decimalInterest != null && grossAcres != null) return Number(o.decimalInterest) * grossAcres;
        return undefined;
    };

    const leaseNetAcres = (lh: Leasehold): number | undefined =>
        ownershipNetAcres(lh.ownershipId != null ? ownershipById[lh.ownershipId] : undefined);

    // A WI owner's slice of a lease = the lease's net acres x that owner's
    // LeaseholdOwnership.DecimalInterest. With no decimals recorded, a lone WI owner takes
    // the whole lease; several owners with no decimals stay blank rather than each showing
    // the full lease acreage as if it were theirs.
    const wiRowNetAcres = (lh: Leasehold, ownerLookUpId: number | undefined, wiOwnerCount: number): number | undefined => {
        const leaseNet = leaseNetAcres(lh);
        if (leaseNet === undefined) return undefined;
        const share = (lh.leaseholdOwnerships || []).find((lo) => lo && ownerLookUpId != null && lo.ownerLookUpId === ownerLookUpId);
        if (share?.decimalInterest != null) return leaseNet * Number(share.decimalInterest);
        return wiOwnerCount <= 1 ? leaseNet : undefined;
    };

    // In-hand leases only. A tract commonly carries a rough draft per mineral owner — records
    // with isActiveLease = true whose lease was never executed (isInHand false) — and listing
    // those alongside real leaseholds makes a mostly-open tract look fully leased. Drafts hold
    // no acreage (dbo.setLeasedStatus ignores them too), so they belong in the leasing screens,
    // not in this summary. Counted below so the caption can say they exist.
    //
    // isInHand comes from LeaseholdDto and is only present on this endpoint's payload; if it
    // is absent altogether (older server), fall back to showing every lease rather than an
    // empty tab.
    const hasInHandFlag = leaseholds.some((lh) => lh.isInHand !== undefined);
    const shownLeaseholds = hasInHandFlag ? leaseholds.filter((lh) => lh.isInHand) : leaseholds;
    const hiddenDrafts = leaseholds.length - shownLeaseholds.length;

    // One row per WI owner on each shown lease, so a lease with two WI owners shows under both,
    // each with its own slice. A lease with no WI owner recorded still shows once, under its own
    // heading, so no lease silently disappears.
    type LeaseRow = { lh: Leasehold; wiOwner: string; netAcres: number | undefined; key: string };
    const rows: LeaseRow[] = [];
    shownLeaseholds.forEach((lh, i) => {
        const wiOwners = (lh.wiOwners || []).filter((o) => !!o?.name);
        if (wiOwners.length === 0) {
            rows.push({ lh, wiOwner: '', netAcres: leaseNetAcres(lh), key: `${lh.id ?? i}-none` });
        } else {
            wiOwners.forEach((o, j) =>
                rows.push({
                    lh,
                    wiOwner: o.name,
                    netAcres: wiRowNetAcres(lh, o.ownerLookUpId, wiOwners.length),
                    key: `${lh.id ?? i}-${j}`,
                })
            );
        }
    });

    // Valid leases first (active and, when the payload says, in hand), then largest net acres
    // first within each group. Rows with no net acres sink to the bottom of their group.
    const isValidLease = (lh: Leasehold): boolean => !!lh.isActiveLease && lh.isInHand !== false;
    // Whether a lease holds today: the MOR's status when the report lists it (it follows the lease
    // dates), else the stored record's active and in-hand flags.
    const morFor = (lh: Leasehold): IMorLease | undefined =>
        (lh.id !== undefined && lh.id !== null ? morById.get(lh.id) : undefined) ?? (lh.leaseId ? morByLeaseId.get(lh.leaseId) : undefined);
    const leaseHeld = (lh: Leasehold): boolean => {
        const mor = morFor(lh);
        return mor ? morHeld(mor.status) : isValidLease(lh);
    };
    rows.sort((a, b) => {
        const validDiff = Number(leaseHeld(b.lh)) - Number(leaseHeld(a.lh));
        if (validDiff !== 0) return validDiff;
        return (b.netAcres ?? -1) - (a.netAcres ?? -1);
    });

    // --- Open acreage ---
    // Leased acreage is the net mineral acres of the interests held today; open acreage is the rest
    // of the tract. With the MOR's owner listing, held is the report's status for each interest,
    // counted the way Mineral Owners counts it (countsTowardTotals: top-level owners, heirs and
    // non-executive interests, as the app's acreage rollup does), so the two tabs agree. Without the listing, the ownerships the app
    // considers LEASED (isLeasedStatus), which only change when a lease is saved. Measured per
    // ownership, not per lease row: an ownership carries one status however many lease records
    // hang off it, so there is nothing to dedupe, and a lease that was never taken in hand
    // correctly contributes nothing. A 100-ac tract with one leased 12.5% interest is 12.5 ac
    // leased and 87.5 ac open even if the other four interests all have active lease records.
    const morOwnerRows = morOwners && morOwners.status === 'ready' && morOwners.rows.length > 0 ? morOwners.rows : undefined;
    let leasedNetAcres = 0;
    if (morOwnerRows) {
        ownerFamilies(morOwnerRows, ownerships || []).forEach((family) =>
            [family.head, ...family.children].forEach((card) => {
                if (!card.held || !countsTowardTotals(card)) return;
                const net =
                    card.netAcres ?? (card.decimalInterest !== undefined && grossAcres != null ? card.decimalInterest * grossAcres : undefined);
                if (net !== undefined) {
                    leasedNetAcres += net;
                }
            })
        );
    } else {
        (ownerships || []).forEach((o) => {
            if (!isLeasedStatus(o?.leasedStatus)) return;
            const net = ownershipNetAcres(o);
            if (net !== undefined) {
                leasedNetAcres += net;
            }
        });
    }

    // Only claim open acreage when we have both a gross-acre total and the horizon's
    // ownerships to net against it; otherwise the figure would be a guess.
    const canComputeOpen = grossAcres != null && grossAcres > 0 && (!!morOwnerRows || (ownerships || []).length > 0);
    const openAcres = canComputeOpen ? Math.max(0, (grossAcres as number) - leasedNetAcres) : undefined;
    const showOpenRow = openAcres !== undefined && openAcres > OPEN_ACRES_EPSILON;

    if (rows.length === 0 && !showOpenRow) {
        return (
            <div className="attr-empty">
                {hiddenDrafts > 0
                    ? `No leases in hand for this tract (${hiddenDrafts} draft ${hiddenDrafts === 1 ? 'lease' : 'leases'} not shown).`
                    : 'No leaseholds found for this tract.'}
            </div>
        );
    }

    // Grouped under each WI owner: a heading with its map color, how many mineral owners it holds
    // leases from, and its net acres on valid leases; then those owners' leases as one-line cards
    // that open into the lease's details, in the order above (valid first, largest slice first).
    // WI owners with the most acreage come first; leases with no WI owner recorded gather last.
    type WiGroup = { wiOwner: string; rows: LeaseRow[]; netAcres: number };
    const groups: WiGroup[] = [];
    const groupOf = new Map<string, WiGroup>();
    rows.forEach((row) => {
        let group = groupOf.get(row.wiOwner);
        if (!group) {
            group = { wiOwner: row.wiOwner, rows: [], netAcres: 0 };
            groupOf.set(row.wiOwner, group);
            groups.push(group);
        }
        group.rows.push(row);
        if (leaseHeld(row.lh)) {
            group.netAcres += row.netAcres ?? 0;
        }
    });
    groups.sort(
        (a, b) =>
            Number(!a.wiOwner) - Number(!b.wiOwner) ||
            b.netAcres - a.netAcres ||
            a.wiOwner.localeCompare(b.wiOwner, undefined, { sensitivity: 'base' })
    );

    // A lease card's details, the same fields on every card, a blank one as a dash: the MOR's when it
    // lists the lease (status in the report's words, terms, HBP/HBU, recording, operator), else the
    // stored record's, as for a lease not yet in hand, which the MOR leaves off. Held By, Operator,
    // Well, Unit, API # and a bonus show only when they say something (a $0.00 bonus doesn't): most
    // leases have none.
    const hasValue = (value: unknown): boolean => value !== null && value !== undefined;
    const HIDDEN_WHEN_BLANK = ['Held By', 'Bonus/Acre', 'Operator', 'Well', 'Unit', 'API #'];
    const shownRows = (rows: KvRow[]): KvRow[] => rows.filter((row) => row.value !== '' || HIDDEN_WHEN_BLANK.indexOf(row.label) < 0);
    const term = (length: number | undefined, units: string | undefined): string =>
        length ? `${length} ${(units || '').trim().toLowerCase()}`.trim() : '';
    const leaseRows = (lh: Leasehold, withLessee: boolean): KvRow[] => {
        const mor = morFor(lh);
        const bonus = { label: 'Bonus/Acre', value: lh.bonusPerAcre ? formatCurrency(lh.bonusPerAcre) : '' };
        if (!mor) {
            const option = term(lh.optionTerm, lh.optionTermUnits);
            return shownRows([
                { label: 'Status', value: (lh.status || '').trim() || (!lh.isActiveLease ? 'Inactive' : lh.isInHand ? 'Active' : 'Not in hand') },
                { label: 'Lessee', value: withLessee ? lesseeOf(lh) : '' },
                { label: 'Royalty', value: hasValue(lh.royalty) ? formatPercent(lh.royalty) : '' },
                { label: 'Lease Date', value: formatDate(infoFor(lh)?.leaseDate || lh.leaseDate) },
                { label: 'Effective', value: formatDate(lh.effectiveDate) },
                { label: 'Expiration', value: formatDate(lh.expirationDate) },
                { label: 'Primary Term', value: term(lh.primaryTerm, lh.primaryTermUnits) },
                { label: 'Option', value: option ? `${option}${lh.optionExercised ? ' (exercised)' : ''}` : '' },
                { label: 'Held By', value: [lh.hbp ? 'HBP' : '', lh.hbu ? 'HBU' : ''].filter(Boolean).join(', ') },
                bonus,
                {
                    label: 'Recording',
                    value: [lh.book ? `Book ${lh.book}` : '', lh.page ? `Page ${lh.page}` : '', lh.instrumentNumber ? `Inst. ${lh.instrumentNumber}` : '']
                        .filter(Boolean)
                        .join(', '),
                },
                { label: 'Operator', value: (lh.operator || '').trim() },
                { label: 'Well', value: (lh.wellName || '').trim() },
                { label: 'Unit', value: (lh.unitName || '').trim() },
                { label: 'API #', value: (lh.apiNumber || '').trim() },
            ]);
        }
        const royalty = hasValue(mor.royalty) ? mor.royalty : lh.royalty;
        return shownRows([
            { label: 'Status', value: mor.status || '' },
            { label: 'Lessee', value: (mor.lessee || '').trim() || (withLessee ? lesseeOf(lh) : '') },
            { label: 'Royalty', value: hasValue(royalty) ? formatPercent(royalty) : '' },
            { label: 'Lease Date', value: formatDate(mor.leaseDate || infoFor(lh)?.leaseDate || lh.leaseDate) },
            { label: 'Effective', value: formatDate(mor.effectiveDate) },
            { label: 'Expiration', value: formatDate(mor.expirationDate || lh.expirationDate) },
            { label: 'Primary Term', value: mor.primaryTerm || '' },
            { label: 'Option', value: mor.optionTerm ? `${mor.optionTerm}${mor.optionExercised ? ' (exercised)' : ''}` : '' },
            { label: 'Held By', value: [mor.hbp ? 'HBP' : '', mor.hbu ? 'HBU' : ''].filter(Boolean).join(', ') },
            bonus,
            { label: 'Recording', value: mor.recordingInfo || '' },
            { label: 'Operator', value: mor.operator || '' },
            { label: 'Well', value: mor.wellName || '' },
            { label: 'Unit', value: mor.unitName || '' },
            { label: 'API #', value: mor.apiNumber || '' },
        ]);
    };
    const items: React.ReactNode[] = [];
    groups.forEach((group) => {
        const owners = new Set(group.rows.map((row) => lessorName(row.lh))).size;
        items.push(
            <GroupHeading
                key={`wi-${group.wiOwner}`}
                name={group.wiOwner || 'No WI owner recorded'}
                rgba={group.wiOwner ? wiOwnerColor(group.wiOwner) : undefined}
                summary={
                    <>
                        {owners} {owners === 1 ? 'owner' : 'owners'}
                        {group.netAcres > 0 && (
                            <>
                                {' · '}
                                <AcresOfGross acres={group.netAcres} gross={grossAcres} />
                            </>
                        )}
                    </>
                }
                collapsed={isGroupCollapsed(group.wiOwner)}
                onToggle={() => toggleGroup(group.wiOwner)}
            />
        );
        if (isGroupCollapsed(group.wiOwner)) {
            return;
        }
        group.rows.forEach(({ lh, netAcres, key }) =>
            items.push(
                <ExpandableCard
                    key={key}
                    className="attr-group-card"
                    title={lessorName(lh) || '—'}
                    aside={
                        <>
                            {netAcres !== undefined && <AcresOfGross acres={netAcres} gross={grossAcres} />}
                            {!leaseHeld(lh) && <span className="attr-muted-note"> {(morFor(lh)?.status || 'inactive').toLowerCase()}</span>}
                        </>
                    }
                    rows={leaseRows(lh, !group.wiOwner)}
                />
            )
        );
    });

    return (
        <div className="attr-card-list">
            {morFailed && (
                <div className="owners-mor-note">
                    Couldn&apos;t load the ownership report&apos;s lease details for this tract, so these cards show the stored lease records.{' '}
                    <button type="button" className="attribute-table-link-btn" onClick={onMorShown}>
                        Try again
                    </button>
                </div>
            )}
            {morOwnersFailed && (
                <div className="owners-mor-note">
                    Couldn&apos;t load the ownership report&apos;s owner statuses for this tract, so the leased and open acres count the stored
                    statuses.{' '}
                    <button type="button" className="attribute-table-link-btn" onClick={onMorOwnersShown}>
                        Try again
                    </button>
                </div>
            )}
            <PagedCards cards={items} />
            {/* Open acreage as a heading of its own, like each WI owner's, in the Leasehold layer's
                color for it, so the list accounts for the whole tract, not just the leased part. */}
            {showOpenRow && (
                <GroupHeading
                    key="open-acreage"
                    name="Open"
                    rgba={leaseholdColors?.__UNLEASED__}
                    summary={<AcresOfGross acres={openAcres as number} gross={grossAcres} />}
                    caretSpace={rows.length > 0}
                />
            )}
            {canComputeOpen && (
                <div className="attr-table-caption">
                    {`Gross ${formatAcres(grossAcres as number)} ac · Leased ${formatAcres(leasedNetAcres)} ac · Open ${formatAcres(
                        openAcres as number
                    )} ac.`}
                    {morOwnerRows
                        ? ' Leased counts the net mineral acres the ownership report shows held today (leased, HBP, HBU, ...).'
                        : ' Leased counts the net mineral acres of ownerships whose leased status is set — an active lease, in hand, in term.'}
                    {hiddenDrafts > 0 &&
                        ` ${hiddenDrafts} draft lease record${hiddenDrafts === 1 ? '' : 's'} (not in hand) ${
                            hiddenDrafts === 1 ? 'is' : 'are'
                        } not listed; that acreage counts as open until the lease is in hand.`}
                </div>
            )}
        </div>
    );
};

/* Leaseholds Brine: the tract's brine leases as the Brine MOR lists them, under a heading per WI owner:
 * who holds the lease, which the listing's lessee isn't always. Each heading has the owner's Brine
 * layer color, how many mineral owners it holds leases from, and its net acres on held leases (the
 * control summary's figures, so they add up). Under it, held leases first (then the newest), each a
 * one-line card (the lessor and the lease's status) that opens into the lease; a lease with two WI
 * owners shows under both. Last, an Open heading for the acreage no brine lease holds. */
const LeaseholdsBrineTab: React.FC<{
    leases: IMorListing<IMorLease> | undefined;
    /** Asks for the Brine MOR listing: when the tab shows, and again on Try again. Stable identity. */
    onShown: () => void;
    /** Brine layer WI owner colors, only while that layer is active (see MapLegacy onContractLayerColors). */
    brineColors?: Record<string, number[]>;
    /** Tract gross acres: the total the leases are measured against. */
    grossAcres?: number;
    /** Each WI owner's net acres on held brine leases (Brine_Owner_Acres, see ownerNetAcres). */
    ownerAcres?: Map<string, number>;
    /** Net acres no brine lease holds (Brine_Open_NetAcres). */
    openAcres?: number;
}> = ({ leases, onShown, brineColors, grossAcres, ownerAcres, openAcres }) => {
    React.useEffect(() => {
        onShown();
    }, [onShown]);
    const [isGroupCollapsed, toggleGroup] = useCollapsedGroups();
    if (!leases || leases.status === 'loading') return <div className="attr-loading">Loading brine leases...</div>;
    if (leases.status === 'error') {
        return (
            <div className="attr-empty">
                Couldn&apos;t load the brine leases for this tract.{' '}
                <button type="button" className="attribute-table-link-btn" onClick={onShown}>
                    Try again
                </button>
            </div>
        );
    }
    const showOpen = openAcres !== undefined && openAcres > OPEN_ACRES_EPSILON;
    if (leases.rows.length === 0 && !showOpen) return <div className="attr-empty">No brine leases found for this tract.</div>;

    const time = (d: string | null | undefined): number => (d ? moment(d).valueOf() || 0 : 0);
    const sorted = [...leases.rows].sort(
        (a, b) =>
            Number(morHeld(b.status)) - Number(morHeld(a.status)) ||
            time(b.leaseDate) - time(a.leaseDate) ||
            (a.lessor || '').localeCompare(b.lessor || '', undefined, { sensitivity: 'base' })
    );

    // WI owners with the most acreage first; leases with no WI owner recorded gather last.
    type BrineGroup = { wiOwner: string; leases: IMorLease[] };
    const groups: BrineGroup[] = [];
    const groupOf = new Map<string, BrineGroup>();
    sorted.forEach((lease) => {
        const wiOwners = (lease.wiOwners || []).filter((name) => !!name);
        (wiOwners.length > 0 ? wiOwners : ['']).forEach((wiOwner) => {
            let group = groupOf.get(wiOwner);
            if (!group) {
                group = { wiOwner, leases: [] };
                groupOf.set(wiOwner, group);
                groups.push(group);
            }
            group.leases.push(lease);
        });
    });
    const acresOf = (wiOwner: string): number => (wiOwner && ownerAcres?.get(wiOwner)) || 0;
    groups.sort(
        (a, b) =>
            Number(!a.wiOwner) - Number(!b.wiOwner) ||
            acresOf(b.wiOwner) - acresOf(a.wiOwner) ||
            a.wiOwner.localeCompare(b.wiOwner, undefined, { sensitivity: 'base' })
    );
    const ownerColor = (name: string): number[] | undefined => (brineColors ? brineColors[name] ?? brineColors.__OTHER__ : undefined);
    const lessorOf = (lease: IMorLease): string => (lease.lessor || '').trim();

    // Every field of the Brine MOR's lease, a blank one as a dash.
    const leaseRows = (lease: IMorLease): KvRow[] => [
        { label: 'Status', value: lease.status || '' },
        { label: 'Lessee', value: (lease.lessee || '').trim() },
        { label: 'Lease #', value: (lease.leaseNumber || '').trim() },
        { label: 'Royalty', value: lease.royalty !== null && lease.royalty !== undefined ? formatPercent(lease.royalty) : '' },
        { label: 'Lease Date', value: formatDate(lease.leaseDate) },
        { label: 'Effective', value: formatDate(lease.effectiveDate) },
        { label: 'Expiration', value: formatDate(lease.expirationDate) },
        { label: 'Primary Term', value: lease.primaryTerm || '' },
        { label: 'Option', value: lease.optionTerm || '' },
        { label: 'Recording', value: lease.recordingInfo || '' },
    ];
    const items: React.ReactNode[] = [];
    groups.forEach((group) => {
        const owners = new Set(group.leases.map(lessorOf)).size;
        const acres = acresOf(group.wiOwner);
        items.push(
            <GroupHeading
                key={`wi-${group.wiOwner}`}
                name={group.wiOwner || 'No WI owner recorded'}
                rgba={group.wiOwner ? ownerColor(group.wiOwner) : undefined}
                summary={
                    <>
                        {owners} {owners === 1 ? 'owner' : 'owners'}
                        {acres > 0 && (
                            <>
                                {' · '}
                                <AcresOfGross acres={acres} gross={grossAcres} />
                            </>
                        )}
                    </>
                }
                collapsed={isGroupCollapsed(group.wiOwner)}
                onToggle={() => toggleGroup(group.wiOwner)}
            />
        );
        if (isGroupCollapsed(group.wiOwner)) {
            return;
        }
        group.leases.forEach((lease, i) =>
            items.push(
                <ExpandableCard
                    key={`brine-${group.wiOwner}-${lease.leaseholdId}-${i}`}
                    className="attr-group-card"
                    title={lessorOf(lease) || `Lease ${lease.leaseNumber || lease.leaseholdId}`}
                    aside={
                        morHeld(lease.status) ? (
                            lease.status || ''
                        ) : (
                            <span className="attr-muted-note">{(lease.status || 'not held').toLowerCase()}</span>
                        )
                    }
                    rows={leaseRows(lease)}
                />
            )
        );
    });
    return (
        <div className="attr-card-list">
            <PagedCards cards={items} />
            {/* Open acreage as a heading of its own, in the Brine layer's color for it, so the list
                accounts for the whole tract, not just the leased part. */}
            {showOpen && (
                <GroupHeading
                    key="open-acreage"
                    name="Open"
                    rgba={brineColors?.__UNLEASED__}
                    summary={<AcresOfGross acres={openAcres as number} gross={grossAcres} />}
                    caretSpace={groups.length > 0}
                />
            )}
        </div>
    );
};

const TractsListTab: React.FC<{
    tracts: Record<string, string | number>[];
    selectedTractId: string | undefined;
    onSelectTract?: (id: string, attributes: Record<string, string | number>) => void;
    selectedTractIds: string[];
    onSelectedTractIdsChange?: (ids: string[]) => void;
    tractLayerIds?: string[];
}> = ({ tracts, selectedTractId, onSelectTract, selectedTractIds, onSelectedTractIdsChange, tractLayerIds }) => {
    /*
     * The working interest owner columns follow the map's lease layers, as the side panel's Leaseholds
     * tabs do: oil and gas (WI_Owner) while the Leaseholds O&G layer draws, or before the layers are
     * known; brine ({prefix}_Owner) while a Leaseholds Brine layer draws. With both, each column says
     * which it is. A brine project such as West Oak (41.16) otherwise read Unleased on every tract,
     * which in oil and gas terms it is.
     */
    const wiColumns = React.useMemo(() => {
        const brineLayer = tractLayerIds?.find(isBrineLayerId);
        const showOilGas = !tractLayerIds || tractLayerIds.indexOf(LEASEHOLD_LAYER_ID) >= 0;
        const columns: { field: string; label: string }[] = [];
        if (showOilGas) {
            columns.push({ field: OIL_AND_GAS_LAYER_FIELDS.owner, label: brineLayer ? 'O&G WI Owner' : 'WI Owner' });
        }
        if (brineLayer) {
            columns.push({ field: contractLayerFields(contractLayerPrefixOf(brineLayer) || '').owner, label: 'Brine WI Owner' });
        }
        return columns;
    }, [tractLayerIds]);
    /*
     * Row budget. Every row re-renders on each selection change, so a few thousand <tr> at
     * once makes multi-select sluggish — hence a starting cap rather than the whole list.
     *
     * But a cap alone stranded people: with 1,210 tracts sorted by KuduTractID there was no
     * way to reach tract 669 at all. So the cap is now a starting point, not a ceiling —
     * search narrows the list before the cap applies, and scrolling to the bottom loads the
     * next ROW_BUDGET_STEP rows. There is deliberately no "Show all": on a multi-thousand-tract
     * project it rendered every row at once and froze the page.
     */
    const ROW_BUDGET = 150;
    const ROW_BUDGET_STEP = 50;

    const getKuduId = React.useCallback((t: Record<string, string | number>) => String(t.KuduTractID || t.KuduTract_ID || ''), []);

    const [search, setSearch] = React.useState('');
    const [rowLimit, setRowLimit] = React.useState(ROW_BUDGET);

    /*
     * Two pieces of state on purpose: `searchInput` is what's typed, `search` is what's
     * actually applied. Filtering on every keystroke re-rendered the whole visible list per
     * character — unusable once "Show all" had been clicked on a 1,200-tract project — so the
     * filter only runs when Search is pressed (button or Enter).
     */
    const [searchInput, setSearchInput] = React.useState('');

    // Applying a search resets the row budget: a narrow result set should start small again so
    // clearing it later can't dump every row back into the DOM unannounced.
    const applySearch = () => {
        setSearch(searchInput);
        setRowLimit(ROW_BUDGET);
    };

    const clearSearch = () => {
        setSearchInput('');
        setSearch('');
        setRowLimit(ROW_BUDGET);
    };

    // Dedupe + sort once per tracts-list change. Re-running these on every render
    // (which happens on every selection-change) is unnecessary work for big projects.
    const sortedTracts = React.useMemo(() => {
        const seenIds = new Set<string>();
        const unique = tracts.filter((t) => {
            const id = getKuduId(t);
            if (!id || seenIds.has(id)) {
                return false;
            }
            seenIds.add(id);
            return true;
        });
        return unique.sort((a, b) => getKuduId(a).localeCompare(getKuduId(b), undefined, { numeric: true, sensitivity: 'base' }));
    }, [tracts, getKuduId]);

    // "Show only the multi-selected tracts" toggle. Defaults to ON whenever a non-empty
    // selection exists so the user sees the relationship immediately; they can flip it
    // off to scan the rest of the project without losing the selection.
    const [showSelectedOnly, setShowSelectedOnly] = React.useState(true);
    React.useEffect(() => {
        // When selection becomes non-empty, default the filter back ON. Leaving it OFF
        // when the user has just selected feels confusing.
        if (selectedTractIds.length > 0) {
            setShowSelectedOnly(true);
        }
    }, [selectedTractIds.length > 0]);

    if (tracts.length === 0) return <div className="attr-empty">No tracts loaded.</div>;

    const selectedSet = new Set(selectedTractIds);
    const hasSelection = selectedTractIds.length > 0;
    const filterActive = hasSelection && showSelectedOnly;
    const selectionFiltered = filterActive ? sortedTracts.filter((t) => selectedSet.has(getKuduId(t))) : sortedTracts;

    // Search across every column the table shows, so "669", "A-485" or an owner name all
    // work. Applied BEFORE the row cap, which is what makes a specific tract reachable
    // however long the list is.
    const needle = search.trim().toLowerCase();
    const matchesSearch = (t: Record<string, string | number>): boolean =>
        [
            getKuduId(t),
            t.County_State,
            t.Location,
            t.Tract_Name,
            t.Legal_Acre ?? t.Legal_Acres,
            t.Title_Status,
            t.Lease_Status,
            ...wiColumns.map((c) => t[c.field]),
        ].some(
            (v) => v !== null && v !== undefined && String(v).toLowerCase().indexOf(needle) >= 0
        );

    const filteredTracts = needle === '' ? selectionFiltered : selectionFiltered.filter(matchesSearch);
    // Selections are always shown in full; everything else honours the current row limit.
    const isCapped = !filterActive && filteredTracts.length > rowLimit;
    const displayTracts = isCapped ? filteredTracts.slice(0, rowLimit) : filteredTracts;

    const removeFromSelection = (id: string) => {
        if (!onSelectedTractIdsChange) return;
        onSelectedTractIdsChange(selectedTractIds.filter((x) => x !== id));
    };
    const clearAll = () => onSelectedTractIdsChange?.([]);

    return (
        <div className="tracts-tab-wrap">
            {hasSelection && (
                <div className="tracts-selection-strip">
                    <span className="tracts-selection-count">
                        {selectedTractIds.length} tract{selectedTractIds.length === 1 ? '' : 's'} selected
                    </span>
                    <label className="tracts-selection-toggle">
                        <input type="checkbox" checked={showSelectedOnly} onChange={(e) => setShowSelectedOnly(e.target.checked)} />
                        Show only selected
                    </label>
                    {/* Bulk actions — wired in Phase 3 */}
                    <div className="tracts-selection-actions">
                        <button type="button" className="tracts-selection-action" disabled title="Coming soon">
                            Add tag
                        </button>
                        <button type="button" className="tracts-selection-action" disabled title="Coming soon">
                            Add note
                        </button>
                        <button type="button" className="tracts-selection-action" disabled title="Coming soon">
                            Report
                        </button>
                    </div>
                    <button type="button" className="tracts-selection-clear" onClick={clearAll}>
                        Clear
                    </button>
                </div>
            )}

            <div className="tracts-search-strip">
                {/* type="text", not "search": the native clear (×) would only wipe the input,
                    leaving the applied filter in place and the box looking empty. */}
                <input
                    type="text"
                    className="tracts-search-input"
                    placeholder="Search tracts — id, county, location, name, status, WI owner"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            applySearch();
                        }
                    }}
                />
                <button type="button" className="tracts-search-button" onClick={applySearch}>
                    Search
                </button>
                {search !== '' && (
                    <button type="button" className="tracts-search-clear" onClick={clearSearch}>
                        Clear
                    </button>
                )}
                <span className="tracts-search-count">
                    {needle === ''
                        ? `${filteredTracts.length.toLocaleString()} tract${filteredTracts.length === 1 ? '' : 's'}`
                        : `${filteredTracts.length.toLocaleString()} of ${selectionFiltered.length.toLocaleString()} match “${search.trim()}”`}
                </span>
            </div>

            {isCapped && (
                <div className="tracts-cap-notice">
                    Showing {displayTracts.length.toLocaleString()} of {filteredTracts.length.toLocaleString()} tracts — scroll down for more.
                    <span className="tracts-cap-hint">Searching or selecting on the map is faster than scrolling.</span>
                </div>
            )}

            <div
                className="attr-table-scroll"
                onScroll={(e) => {
                    // Near the bottom: load the next batch. Guarded by isCapped so a fully
                    // loaded (or filtered) list never grows past its rows.
                    const el = e.currentTarget;
                    if (isCapped && el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
                        setRowLimit((limit) => limit + ROW_BUDGET_STEP);
                    }
                }}
            >
                <table className="attr-data-table">
                    <colgroup>
                        {filterActive && <col className="col-remove" />}
                        <col className="col-tract-no" />
                        <col className="col-kudu" />
                        <col className="col-county-state" />
                        <col className="col-location" />
                        <col className="col-tract-name" />
                        <col className="col-acres" />
                        <col className="col-title" />
                        <col className="col-lease" />
                        {wiColumns.map((c) => (
                            <col key={c.field} className="col-wi-owner" />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {filterActive && <th aria-label="Remove from selection" />}
                            <th>Tract</th>
                            <th>KuduTractID</th>
                            <th>County / State</th>
                            <th>Location</th>
                            <th>Tract Name</th>
                            <th className="num">Acres</th>
                            <th>Title Status</th>
                            <th>Lease Status</th>
                            {wiColumns.map((c) => (
                                <th key={c.field}>{c.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayTracts.map((t, idx) => {
                            const id = getKuduId(t);
                            const isClickSelected = id === selectedTractId;
                            const isInSelection = selectedSet.has(id);
                            // Tract number = the KuduTractID part just before the dash ("1516.1.3.5-1" -> "5").
                            const tractNo = id.split('-')[0].split('.')[3] || '';
                            const acresRaw = t.Legal_Acre ?? t.Legal_Acres;
                            const acres = acresRaw === undefined || acresRaw === '' ? '' : formatAcres(Number(acresRaw)) || String(acresRaw);
                            const cell = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
                            return (
                                <tr
                                    key={id || idx}
                                    onClick={() => id && onSelectTract && onSelectTract(id, t)}
                                    className={
                                        [isInSelection ? 'tracts-row-selected' : '', isClickSelected ? 'tracts-row-active' : ''].join(' ').trim() ||
                                        undefined
                                    }
                                    style={{ cursor: onSelectTract ? 'pointer' : undefined }}
                                >
                                    {filterActive && (
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="tracts-row-remove"
                                                title="Remove from selection"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeFromSelection(id);
                                                }}
                                            >
                                                ×
                                            </button>
                                        </td>
                                    )}
                                    {/* title= gives the full value on hover where a fixed-width cell clips it */}
                                    <td className="tract-no">{tractNo}</td>
                                    <td className="muted">{id}</td>
                                    <td title={cell(t.County_State)}>{cell(t.County_State)}</td>
                                    <td title={cell(t.Location)}>{cell(t.Location)}</td>
                                    <td title={cell(t.Tract_Name)}>{cell(t.Tract_Name)}</td>
                                    <td className="num">{acres}</td>
                                    <td title={cell(t.Title_Status)}>{cell(t.Title_Status)}</td>
                                    <td title={cell(t.Lease_Status)}>{cell(t.Lease_Status)}</td>
                                    {wiColumns.map((c) => {
                                        const owners = formatWiOwners(t[c.field]);
                                        return (
                                            <td
                                                key={c.field}
                                                title={
                                                    cell(t[c.field])
                                                        .split('|')
                                                        .map((s) => s.trim())
                                                        .filter(Boolean)
                                                        .join(', ') || owners
                                                }
                                            >
                                                {owners}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {displayTracts.length === 0 &&
                    (needle !== '' ? (
                        <div className="attr-empty">
                            No tract matches “{search}”{filterActive ? ' in the current selection' : ` in this project's loaded tract list`}.
                        </div>
                    ) : (
                        filterActive && <div className="attr-empty">No selected tracts match this project&apos;s loaded tract list.</div>
                    ))}
            </div>
        </div>
    );
};

const TRACTS_PANEL_HEIGHT_KEY = 'landhawk.mapping.tractsPanelHeight';
const TRACTS_PANEL_MIN_HEIGHT = 120;
const TRACTS_PANEL_DEFAULT_HEIGHT = 280;

const readStoredTractsPanelHeight = (): number => {
    try {
        const n = Number(window.localStorage.getItem(TRACTS_PANEL_HEIGHT_KEY));
        return n >= TRACTS_PANEL_MIN_HEIGHT ? n : TRACTS_PANEL_DEFAULT_HEIGHT;
    } catch {
        return TRACTS_PANEL_DEFAULT_HEIGHT;
    }
};

/* Bottom pane: the project's tract list only. Per-tract detail lives in TractDetailPanel,
 * docked to the right of the map. */
const TractAttributeTable: React.FC<TractAttributeTableProps> = ({
    tractId,
    isCollapsed,
    onToggleCollapse,
    allTracts,
    onSelectTract,
    selectedTractIds,
    onSelectedTractIdsChange,
    tractLayerIds,
}) => {
    const [height, setHeight] = React.useState<number>(readStoredTractsPanelHeight);
    const dragRef = React.useRef<{ startY: number; startHeight: number } | null>(null);
    // Ends the drag in progress, if any. The body class it removes turns off pointer events on the
    // whole page, so every way a drag can stop must end it, not just a mouseup the window hears.
    const endDragRef = React.useRef<(() => void) | null>(null);

    // Drag the top edge to resize. Listeners live on window so the drag survives the pointer
    // leaving the thin handle; the map (a flex sibling above) grows/shrinks to match.
    const onResizeStart = (e: React.MouseEvent) => {
        e.preventDefault();
        endDragRef.current?.();
        dragRef.current = { startY: e.clientY, startHeight: height };
        document.body.classList.add('tracts-panel-resizing');

        const onMove = (ev: MouseEvent) => {
            if (!dragRef.current) return;
            // No button held: the mouseup happened where this page didn't hear it.
            if (ev.buttons === 0) {
                endDragRef.current?.();
                return;
            }
            const maxHeight = Math.max(TRACTS_PANEL_MIN_HEIGHT, window.innerHeight * 0.75);
            const next = dragRef.current.startHeight + (dragRef.current.startY - ev.clientY);
            setHeight(Math.round(Math.min(maxHeight, Math.max(TRACTS_PANEL_MIN_HEIGHT, next))));
        };
        const endDrag = () => {
            dragRef.current = null;
            endDragRef.current = null;
            document.body.classList.remove('tracts-panel-resizing');
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', endDrag);
            window.removeEventListener('blur', endDrag);
        };
        endDragRef.current = endDrag;
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', endDrag);
        // Alt-tab or a dialog taking focus mid-drag: the mouseup may never arrive.
        window.addEventListener('blur', endDrag);
    };

    // Leaving the Mapping page mid-drag mustn't leave the page locked.
    React.useEffect(() => () => endDragRef.current?.(), []);

    React.useEffect(() => {
        try {
            window.localStorage.setItem(TRACTS_PANEL_HEIGHT_KEY, String(height));
        } catch {
            /* storage unavailable — size just won't persist */
        }
    }, [height]);

    return (
        <div className={`attribute-table-panel ${isCollapsed ? 'collapsed' : ''}`} style={isCollapsed ? undefined : { height }}>
            {!isCollapsed && <div className="attribute-table-resize-handle" onMouseDown={onResizeStart} title="Drag to resize" />}
            {/* Thin bar with a centered pull tab, same design language as the side drawer handles. */}
            <div className="attribute-table-header tracts-bar" onClick={onToggleCollapse}>
                <span className="attribute-table-title">Tract List</span>
                <button
                    type="button"
                    className="tracts-bar-toggle"
                    onClick={(e) => {
                        // The bar toggles too; don't let this click toggle twice.
                        e.stopPropagation();
                        onToggleCollapse();
                    }}
                    aria-label={isCollapsed ? 'Show tracts list' : 'Hide tracts list'}
                    aria-expanded={!isCollapsed}
                >
                    <span className={`tracts-bar-chevron ${isCollapsed ? 'up' : 'down'}`}>{'\u203A'}</span>
                </button>
            </div>
            {!isCollapsed && (
                <div className="attribute-table-body">
                    <div className="lh-tab-content">
                        <TractsListTab
                            tracts={allTracts || []}
                            selectedTractId={tractId}
                            onSelectTract={onSelectTract}
                            selectedTractIds={selectedTractIds ?? []}
                            onSelectedTractIdsChange={onSelectedTractIdsChange}
                            tractLayerIds={tractLayerIds}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

type TractDetailPanelProps = Pick<
    TractAttributeTableProps,
    | 'tractId'
    | 'geoJsonAttributes'
    | 'tractDetail'
    | 'mineralOwnerships'
    | 'leaseholds'
    | 'isLoadingDetail'
    | 'isLoadingOwnerships'
    | 'isLoadingLeaseholds'
    | 'isCollapsed'
    | 'onToggleCollapse'
    | 'onNavigateToTractPage'
> & {
    /** Parent Lease date + lessee by lease id, for the Leaseholds tab (see LeaseInfo). */
    leaseInfo: Record<number, LeaseInfo>;
    /** Called with the tract's leaseholds when the Leaseholds tab shows them; stable identity. */
    onLeaseholdsShown: (leaseholds: Leasehold[]) => void;
    /** Loads an owner's contact notes when their Mineral Owners card is opened. */
    onLoadContactNotes: (ownership: Ownership) => Promise<ContactNote[]>;
    /** Contact Category colors as the map resolved them (see MapLegacy onContactCategoryColors). */
    contactCategoryColors?: Record<string, number[]>;
    /** Title Status layer colors; pass only while the Title Status layer is active. */
    titleStatusColors?: Record<string, number[]>;
    /** The tract layer the map is showing; the panel opens on its tab (see tabForLayer). */
    activeLayerId?: string;
    /** The map's tract layers. Tract Info and Mineral Owners always show; Leaseholds O&G and
     *  Leaseholds Brine only while the map has the Leasehold or a Brine layer. Unknown (undefined):
     *  the Leaseholds O&G tab shows. */
    tractLayerIds?: string[];
    /** The selected tract's MOR listings (see Mapping's handleMorShown), and the call that asks for
     *  one when its tab shows. Without onMorShown the tabs read the stored records. */
    morOwners?: IMorListing<IMorOwner>;
    morLeases?: IMorListing<IMorLease>;
    morBrineLeases?: IMorListing<IMorLease>;
    onMorShown?: (listing: 'owners' | 'leases' | 'brine-leases') => void;
    /** Leasehold layer WI owner colors; pass only while the Leasehold layer is active. */
    leaseholdColors?: Record<string, number[]>;
    /** Brine layer WI owner colors; pass only while a Brine layer is active. */
    brineColors?: Record<string, number[]>;
    /** Header text for the selected tract (land-system specific); defaults to "Tract: {id}". */
    title?: React.ReactNode;
    /** Zooms the map to the selected tract. */
    onZoomToTract?: () => void;
    /** Some of the tract's records didn't load, or LandHawk has no such tract (see tractLoadNote). */
    loadNote?: string;
};

const OIL_GAS_TAB = 'Leaseholds O&G';
const BRINE_TAB = 'Leaseholds Brine';
/* The tab for what the map is showing: Title Status → Tract Info; Contact Category, or a contract
 * layer with no tab of its own → Mineral Owners; Leasehold → Leaseholds O&G; Brine → Leaseholds
 * Brine. No layer yet → Tract Info. */
const tabForLayer = (layerId: string | undefined): string =>
    !layerId || layerId === 'tracts-title-status'
        ? 'Tract Info'
        : layerId === LEASEHOLD_LAYER_ID
        ? OIL_GAS_TAB
        : isBrineLayerId(layerId)
        ? BRINE_TAB
        : 'Mineral Owners';

/* Right-hand drawer beside the map: control summary + Tract Info / Mineral Owners / a Leaseholds tab
 * per layer with a MOR listing, for the selected tract. Collapses to a thin strip, like the legend
 * drawer on the left. */
export const TractDetailPanel: React.FC<TractDetailPanelProps> = ({
    tractId,
    geoJsonAttributes,
    tractDetail,
    mineralOwnerships,
    leaseholds,
    leaseInfo,
    onLeaseholdsShown,
    onLoadContactNotes,
    isLoadingOwnerships,
    isLoadingLeaseholds,
    isCollapsed,
    onToggleCollapse,
    onNavigateToTractPage,
    onZoomToTract,
    contactCategoryColors,
    leaseholdColors,
    brineColors,
    title,
    activeLayerId,
    tractLayerIds,
    morOwners,
    morLeases,
    morBrineLeases,
    onMorShown,
    titleStatusColors,
    loadNote,
}) => {
    const [openOwnersOnly, setOpenOwnersOnly] = React.useState(false);
    const [ownerView, setOwnerView] = React.useState<OwnerView>('category');
    // One stable call per listing, for the tabs' shown effects.
    const onMorOwnersShown = React.useCallback(() => onMorShown && onMorShown('owners'), [onMorShown]);
    const mapCategories = React.useMemo(() => ownershipCategories(geoJsonAttributes?.Ownership_Categories), [geoJsonAttributes]);
    const onMorLeasesShown = React.useCallback(() => onMorShown && onMorShown('leases'), [onMorShown]);
    const onMorBrineShown = React.useCallback(() => onMorShown && onMorShown('brine-leases'), [onMorShown]);
    // Expand all / Collapse all, from a right-click on the panel header, a group heading or a card's
    // title line (see CardExpansionContext).
    const [cardsOpen, setCardsOpen] = React.useState(true);
    const [cardsVersion, setCardsVersion] = React.useState(0);
    const showCardMenu = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const setAll = (open: boolean) => {
            setCardsOpen(open);
            setCardsVersion((version) => version + 1);
        };
        ContextMenu.show(
            <Menu>
                <MenuItem icon="expand-all" text="Expand all" onClick={() => setAll(true)} />
                <MenuItem icon="collapse-all" text="Collapse all" onClick={() => setAll(false)} />
            </Menu>,
            { left: e.clientX, top: e.clientY }
        );
    }, []);
    const cardExpansion = React.useMemo<ICardExpansion>(
        () => ({ open: cardsOpen, version: cardsVersion, showMenu: showCardMenu }),
        [cardsOpen, cardsVersion, showCardMenu]
    );

    // Tract Info and Mineral Owners always; a Leaseholds tab for each layer the map has that the MOR
    // lists leases for (oil and gas, and brine).
    const showOilGasTab = !tractLayerIds || tractLayerIds.indexOf(LEASEHOLD_LAYER_ID) >= 0;
    const showBrineTab = !!tractLayerIds && tractLayerIds.some(isBrineLayerId);
    const brineOpen = geoJsonAttributes?.Brine_Open_NetAcres;
    const brineOpenAcres = brineOpen === undefined || brineOpen === '' ? undefined : Number(brineOpen);
    const tabs: JSX.Element[] = [
        <Tab key="info" tabName="Tract Info">
            <TractInfoTab attributes={geoJsonAttributes} tractDetail={tractDetail} tractId={tractId} titleStatusColors={titleStatusColors} />
        </Tab>,
        <Tab key="owners" tabName="Mineral Owners">
            <MineralOwnersTab
                ownerships={mineralOwnerships}
                isLoading={isLoadingOwnerships}
                morOwners={morOwners}
                onMorShown={onMorShown ? onMorOwnersShown : undefined}
                contactCategoryColors={contactCategoryColors}
                openOnly={openOwnersOnly}
                onOpenOnlyChange={setOpenOwnersOnly}
                view={ownerView}
                onViewChange={setOwnerView}
                onLoadContactNotes={onLoadContactNotes}
                mapCategories={mapCategories}
            />
        </Tab>,
    ];
    if (showOilGasTab) {
        tabs.push(
            <Tab key="oil-gas" tabName={OIL_GAS_TAB}>
                {/* Each Leaseholds tab opens on the control summary for its own lease type. */}
                <ControlSummary attributes={geoJsonAttributes} hasTract={!!tractId} />
                <LeaseholdsTab
                    leaseholds={leaseholds}
                    leaseInfo={leaseInfo}
                    onShown={onLeaseholdsShown}
                    isLoading={isLoadingLeaseholds || isLoadingOwnerships}
                    ownerships={mineralOwnerships}
                    grossAcres={tractGrossAcres(tractDetail, geoJsonAttributes)}
                    leaseholdColors={leaseholdColors}
                    morLeases={morLeases}
                    onMorShown={onMorShown ? onMorLeasesShown : undefined}
                    morOwners={morOwners}
                    onMorOwnersShown={onMorShown ? onMorOwnersShown : undefined}
                />
            </Tab>
        );
    }
    if (showBrineTab) {
        tabs.push(
            <Tab key="brine" tabName={BRINE_TAB}>
                <ControlSummary
                    attributes={geoJsonAttributes}
                    hasTract={!!tractId}
                    contractPrefix={contractLayerPrefixOf(tractLayerIds && tractLayerIds.find(isBrineLayerId))}
                />
                <LeaseholdsBrineTab
                    leases={morBrineLeases}
                    onShown={onMorBrineShown}
                    brineColors={brineColors}
                    grossAcres={tractGrossAcres(tractDetail, geoJsonAttributes)}
                    ownerAcres={ownerNetAcres(geoJsonAttributes?.Brine_Owner_Acres)}
                    openAcres={brineOpenAcres}
                />
            </Tab>
        );
    }
    const initialTabIndex = Math.max(
        0,
        tabs.findIndex((tab) => tab.props.tabName === tabForLayer(activeLayerId))
    );
    return (
        <CardExpansionContext.Provider value={cardExpansion}>
            <aside className={`tract-detail-panel ${isCollapsed ? 'collapsed' : ''}`}>
                <button
                    type="button"
                    className="tract-detail-toggle"
                    onClick={onToggleCollapse}
                    aria-label={isCollapsed ? 'Show tract details' : 'Hide tract details'}
                    aria-expanded={!isCollapsed}
                >
                    {isCollapsed ? '\u2039' : '\u203A'}
                </button>
                {!isCollapsed && (
                    <>
                        <div className="attribute-table-header tract-detail-header" onContextMenu={showCardMenu}>
                            <div className="tract-detail-heading">
                                <span className="attribute-table-title">{tractId ? title || `Tract: ${tractId}` : 'Tract Details'}</span>
                                {/* A custom (land-system) title doesn't include the KuduTractID, so keep it
                                    here on every tab, small; the default title already is the ID. */}
                                {tractId && title && (
                                    <button
                                        type="button"
                                        className="tract-detail-kudu"
                                        title="Copy KuduTractID"
                                        onClick={() => {
                                            try {
                                                void navigator.clipboard?.writeText(tractId);
                                            } catch {
                                                /* clipboard unavailable — the ID is still selectable text */
                                            }
                                        }}
                                    >
                                        {tractId}
                                    </button>
                                )}
                            </div>
                            {tractId && (onZoomToTract || onNavigateToTractPage) && (
                                <div className="tract-detail-actions">
                                    {onZoomToTract && (
                                        <button
                                            type="button"
                                            className="attribute-table-link-btn"
                                            onClick={onZoomToTract}
                                            title="Zoom the map to this tract"
                                        >
                                            Zoom to Tract
                                        </button>
                                    )}
                                    {onNavigateToTractPage && (
                                        <button className="attribute-table-link-btn" onClick={onNavigateToTractPage}>
                                            Go to Tract Page &#8599;
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        {!tractId ? (
                            <div className="attr-empty">Select a tract on the map or in the list below.</div>
                        ) : (
                            <>
                                {loadNote && (
                                    <div className="tract-load-note" role="status">
                                        {loadNote}
                                    </div>
                                )}
                                <div className="attribute-table-body">
                                    {/* key={tractId}: each newly selected tract opens on the default tab;
                                    a layer switch changes initialTabIndex, which TabPanel follows.
                                    TabPanel reads every child as a tab, so it gets the built list. */}
                                    <TabPanel key={tractId} alignment={TabPanelAlignment.Horizontal} initialTabIndex={initialTabIndex}>
                                        {tabs}
                                    </TabPanel>
                                </div>
                            </>
                        )}
                    </>
                )}
            </aside>
        </CardExpansionContext.Provider>
    );
};

export default TractAttributeTable;



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/TractAttributeTable.tsx